// CALLSHEET Studio Player — custom masked player that fully owns the visual
// surface. Underlying delivery engine (YouTube/CDN/proprietary) is invisible
// to the user. A persistent CALLSHEET watermark + branded chrome means the
// crew sees a first-party player today and a first-party player tomorrow,
// regardless of how we source the stream.
import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2 } from "lucide-react";

function fmt(t) {
  if (!isFinite(t)) return "0:00";
  const m = Math.floor(t / 60).toString();
  const s = Math.floor(t % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function MaskedVideoPlayer({ src, poster, title = "Audition", ytId }) {
  // Mock-layer fallback. Real CALLSHEET Engine resolves `ytId` server-side
  // and streams via signed-URL proxy → underlying provider is opaque.
  const videoSrc =
    src ||
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4";

  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const onTime = () => setCur(v.currentTime);
    const onLoaded = () => setDur(v.duration);
    const onEnd = () => setPlaying(false);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("ended", onEnd);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = () => {
    const v = ref.current; if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const seek = (e) => {
    const v = ref.current; if (!v) return;
    v.currentTime = (parseFloat(e.target.value) / 100) * (v.duration || 0);
  };
  const fs = () => ref.current?.requestFullscreen?.();

  return (
    <div
      className="masked-video relative w-full aspect-video bg-black rounded-xl overflow-hidden border border-white/10 group select-none"
      data-testid="masked-video-player"
    >
      {/* ============ Stream surface ============ */}
      <video
        ref={ref}
        src={videoSrc}
        poster={poster}
        className="w-full h-full object-cover"
        playsInline
        onClick={toggle}
        data-yt-id={ytId || undefined}
      />

      {/* Pointer event masker — swallows any underlying iframe / provider touches */}
      <div className="absolute inset-0 pointer-events-none" onContextMenu={(e) => e.preventDefault()} />

      {/* ============ CALLSHEET STUDIO TOP BAR ============ */}
      <div className="absolute top-0 inset-x-0 px-4 py-3 bg-gradient-to-b from-black/85 via-black/40 to-transparent flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {/* Mini clapper logo */}
          <span className="relative h-5 w-5 inline-block" aria-hidden>
            <span className="absolute inset-x-0 bottom-0 h-[68%] bg-[#0a0a0a] border border-white/30 rounded-[2px]" />
            <span className="absolute inset-x-0 top-0 h-[36%] origin-bottom-left -rotate-[12deg] cs-clapper-stripes rounded-[2px]" />
          </span>
          <div className="leading-none">
            <div className="font-display font-bold uppercase tracking-tight text-[13px]">
              <span className="text-white">CALL</span>
              <span className="text-[#C0C0C0]">SHEET</span>
              <span className="ml-2 text-[9px] tracking-[0.3em] text-[#F5B82E] uppercase">Studio</span>
            </div>
            <div className="text-[9px] tracking-[0.22em] uppercase text-white/55 mt-0.5 truncate max-w-[280px]">
              {title}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] tracking-[0.3em] uppercase px-2 py-1 rounded bg-white/8 border border-white/15 text-white/75">
            UNLISTED · ENCRYPTED
          </span>
        </div>
      </div>

      {/* ============ Persistent corner watermark — always visible while playing ============ */}
      <div className="absolute top-14 right-4 flex items-center gap-1.5 opacity-50 group-hover:opacity-80 transition-opacity pointer-events-none"
           data-testid="cs-watermark">
        <span className="font-display font-bold uppercase tracking-[0.05em] text-[10px] text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">
          CALL<span className="text-[#C0C0C0]">SHEET</span>
        </span>
      </div>

      {/* ============ Center play indicator (hidden when playing) ============ */}
      {!playing && (
        <button
          onClick={toggle}
          className="absolute inset-0 flex items-center justify-center pointer-events-auto"
          aria-label="Play"
          data-testid="video-center-play"
        >
          <span className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-md border border-white/30 flex items-center justify-center hover:scale-110 transition shadow-2xl">
            <Play size={26} className="text-white ml-1" />
          </span>
        </button>
      )}

      {/* ============ Control bar ============ */}
      <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex items-center gap-3">
        <button
          onClick={toggle}
          className="w-9 h-9 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 transition"
          data-testid="video-play-btn"
        >
          {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
        </button>
        <span className="text-xs text-white/80 tabular-nums">{fmt(cur)}</span>
        <input
          type="range" min={0} max={100} step={0.1}
          value={dur ? (cur / dur) * 100 : 0}
          onChange={seek}
          className="flex-1 accent-[color:var(--association-theme)]"
          data-testid="video-scrub"
        />
        <span className="text-xs text-white/60 tabular-nums">{fmt(dur)}</span>
        <button onClick={() => { const v = ref.current; if (!v) return; v.muted = !v.muted; setMuted(v.muted); }}
          className="text-white/70 hover:text-white" data-testid="video-mute-btn">
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
        <button onClick={fs} className="text-white/70 hover:text-white" data-testid="video-fs-btn">
          <Maximize2 size={16} />
        </button>
      </div>

      {/* ============ Engine signature (bottom-left, subtle) ============ */}
      <div className="absolute bottom-14 left-4 pointer-events-none opacity-40 group-hover:opacity-70 transition-opacity">
        <div className="text-[8px] tracking-[0.32em] uppercase text-white/80">
          Powered by CALLSHEET Engine
        </div>
      </div>
    </div>
  );
}
